/**
 * Sons sintetizados pelo WebAudio API — sem precisar de arquivo .mp3.
 * Vários timbres curtos pra notificação de chat.
 */

export type TipoSom = "ding" | "pop" | "chime" | "blip" | "soft";

export const TIPOS_SOM: Array<{ valor: TipoSom; label: string }> = [
  { valor: "ding", label: "Ding (sino agudo)" },
  { valor: "pop", label: "Pop (suave)" },
  { valor: "chime", label: "Chime (2 notas)" },
  { valor: "blip", label: "Blip (eletrônico)" },
  { valor: "soft", label: "Soft (discreto)" },
];

let ctx: AudioContext | null = null;

function ensureCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => null);
  return ctx;
}

interface NotaCfg {
  freq: number;
  start: number;     // offset em segundos
  duracao: number;   // duração em segundos
  tipo?: OscillatorType;
}

function tocarNotas(notas: NotaCfg[], volume: number) {
  const c = ensureCtx();
  if (!c) return;
  try {
    const t0 = c.currentTime;
    // pico final = volume (0-1) * 0.6 pra evitar distorção do output
    const pico = Math.max(0, Math.min(1, volume)) * 0.6;
    for (const n of notas) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = n.tipo || "sine";
      osc.frequency.setValueAtTime(n.freq, t0 + n.start);
      gain.gain.setValueAtTime(0.0001, t0 + n.start);
      gain.gain.exponentialRampToValueAtTime(pico, t0 + n.start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.duracao);
      osc.connect(gain);
      gain.connect(c.destination);
      osc.start(t0 + n.start);
      osc.stop(t0 + n.start + n.duracao + 0.02);
    }
  } catch {}
}

export function tocarSom(tipo: TipoSom = "ding", volume: number = 0.7) {
  switch (tipo) {
    case "ding":
      tocarNotas(
        [
          { freq: 880, start: 0, duracao: 0.32, tipo: "triangle" },
          { freq: 1320, start: 0.06, duracao: 0.32, tipo: "triangle" },
        ],
        volume,
      );
      break;
    case "pop":
      tocarNotas(
        [{ freq: 520, start: 0, duracao: 0.18, tipo: "sine" }],
        volume,
      );
      break;
    case "chime":
      tocarNotas(
        [
          { freq: 660, start: 0, duracao: 0.4, tipo: "triangle" },
          { freq: 988, start: 0.18, duracao: 0.45, tipo: "triangle" },
        ],
        volume,
      );
      break;
    case "blip":
      tocarNotas(
        [
          { freq: 1200, start: 0, duracao: 0.08, tipo: "square" },
          { freq: 1600, start: 0.1, duracao: 0.08, tipo: "square" },
        ],
        volume * 0.6, // square é mais "agudo"; compensa
      );
      break;
    case "soft":
      tocarNotas(
        [{ freq: 440, start: 0, duracao: 0.5, tipo: "sine" }],
        volume * 0.85,
      );
      break;
  }
}

// Compat: mantido pra não quebrar imports antigos.
export function tocarDing() {
  tocarSom("ding", 0.7);
}
